import React, { useEffect, useRef } from "react";
import {
  useRive,
  useStateMachineInput,
  EventCallback,
  Rive,
} from "@rive-app/react-canvas";
import { Box, Center } from "@chakra-ui/react";

export interface InputConfig {
  name: string;
  value?: boolean | number;
}

interface RiveAnimationProps {
  src: string;
  stateMachineName?: string;
  inputs?: InputConfig[];
  autoplay?: boolean;
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
  buttonsContainerStyle?: React.CSSProperties;
  buttonsContainerClassName?: string;
  onLoad?: ({ rive, riveInputs }: any) => void;
  onPlay?: EventCallback | undefined;
  onPause?: EventCallback | undefined;
  onStop?: EventCallback | undefined;
  repeat?: boolean;
  artboard?: string;
  play?: (rive: Rive | null) => void;
}

const RiveAnimation = ({
  src,
  stateMachineName = "State Machine 1",
  inputs = [],
  autoplay = true,
  width = 400,
  height = 400,
  style = {},
  onLoad,
  onPlay,
  onPause,
  onStop,
  artboard,
  repeat,
  play,
}: RiveAnimationProps) => {
  const { rive, RiveComponent } = useRive({
    src,
    stateMachines: stateMachineName,
    autoplay,
    onPlay,
    onPause,
    onStop,
    artboard,
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (play) {
      play(rive);
    }
  }, [play, rive]);

  const riveInputs = inputs.reduce(
    (acc, inputConfig) => ({
      ...acc,
      [inputConfig.name]: useStateMachineInput(
        rive,
        stateMachineName,
        inputConfig.name,
        inputConfig.value
      ),
    }),
    {}
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (rive && entry.isIntersecting) {
            if (repeat) {
              rive.reset();
              rive.play();
            }
          }
        });
      },
      { threshold: 0.1 } // Trigger at least 10% of the component is visible
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [rive, autoplay]);

  useEffect(() => {
    if (onLoad && rive) {
      onLoad({ rive, riveInputs });
    }
  }, [rive, riveInputs]);

  useEffect(() => {
    if (rive) {
      const cleanup = () => {
        rive.removeRiveListeners();
      };
      return cleanup;
    }
  }, [rive]);

  return (
    <Center>
      <Box ref={containerRef} width={width}>
        <RiveComponent
          style={{
            height,
            ...style,
          }}
        />
      </Box>
    </Center>
  );
};

export default RiveAnimation;
